import React, {
  useRef, useEffect, useState, forwardRef,
} from 'lib/react';
import { useImperativeHandle } from 'react';
import {
  EVGrid,
  EVInputLabel,
  EVTypography,
  EVFormControl,
  EVFormHelperText,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import PropTypes from 'prop-types';

import TextInputField from 'commonComponents/textInputField';
import SelectField from 'commonComponents/select-field';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import { getMonthList, getYearList } from 'utils/date.utils';
import { useSelector } from 'lib/redux';
import generateCaptureContext from 'services/microform.service';
import PaymentInformationStyles from './PaymentInformationStyles';
import { PAYMENT_CARD_TYPE_OPTION, PAYMENT_CARD_TYPE_IDS } from './PaymentInformationConstants';
import PaymentInformationLoader from './Components/PaymentInformationLoader/PaymentInformationLoader.component';
import {
  decodeCaptureContext,
  getTokenOptions,
} from './PaymentInformation.util';

// #genai Extracted from the main forwardRef body to bring its cognitive complexity from 16 → below
// the SonarQube S3776 threshold of 15. The expiration date fieldset carried 4 conditional branches
// (semiBoldLabels label class, and !semiBoldLabels error tinting on each of month/year) that all
// counted toward the outer component's complexity. Rendered markup is byte-identical to the inline
// version — this is a pure structural refactor, no behaviour change. Written as a function
// declaration to satisfy the project's react/function-component-definition ESLint rule.
function CardExpirationSection({
  paymentInfo,
  paymentInfoErrors,
  handleChange,
  semiBoldLabels,
  elementRefInfo,
  classes,
  t,
}) {
  const monthErrorLen = paymentInfoErrors.expirationDateMonth.length;
  const yearErrorLen = paymentInfoErrors.expirationDateYear.length;
  const showLegacyLabelError = !semiBoldLabels;
  return (
    <EVGrid className={classes.fieldContainer} container item direction="column">
      <EVInputLabel
        className={semiBoldLabels ? classes.phase2Label : classes.label}
        classes={{ asterisk: classes.asterisk }}
        required
        error={showLegacyLabelError && (monthErrorLen > 0 || yearErrorLen > 0)}
      >
        {t('payment_information__expiration_date')}
      </EVInputLabel>
      <EVGrid className={classes.expirationContainer} container item>
        <EVGrid className={classes.monthContainer} item>
          <EVInputLabel
            className={classes.label}
            classes={{ asterisk: classes.asterisk }}
            required
            error={showLegacyLabelError && monthErrorLen > 0}
          >
            {t('payment_information__expiration_date_month')}
          </EVInputLabel>
          <SelectField
            selectId="expirationDateMonth"
            variant="outlined"
            fullWidth
            selectedValue={paymentInfo.expirationDateMonth}
            handleChange={(event) => handleChange('expirationDateMonth', event)}
            className={classes.cardTypeOptions}
            selectOptions={getMonthList()}
            error={monthErrorLen > 0}
            errorMessage={paymentInfoErrors.expirationDateMonth}
            innerRef={elementRefInfo.expirationDateMonth.elementRef}
          />
        </EVGrid>
        <EVGrid className={classes.yearContainer} item>
          <EVInputLabel
            className={classes.label}
            classes={{ asterisk: classes.asterisk }}
            required
            error={showLegacyLabelError && yearErrorLen > 0}
          >
            {t('payment_information__expiration_date_year')}
          </EVInputLabel>
          <SelectField
            selectId="expirationDateYear"
            variant="outlined"
            fullWidth
            selectedValue={paymentInfo.expirationDateYear}
            handleChange={(event) => handleChange('expirationDateYear', event)}
            className={classes.cardTypeOptions}
            selectOptions={getYearList()}
            error={yearErrorLen > 0}
            errorMessage={paymentInfoErrors.expirationDateYear}
            innerRef={elementRefInfo.expirationDateYear.elementRef}
          />
        </EVGrid>
      </EVGrid>
    </EVGrid>
  );
}

CardExpirationSection.propTypes = {
  paymentInfo: PropTypes.shape().isRequired,
  paymentInfoErrors: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  semiBoldLabels: PropTypes.bool.isRequired,
  elementRefInfo: PropTypes.shape().isRequired,
  classes: PropTypes.shape().isRequired,
  t: PropTypes.func.isRequired,
};

const PaymentInformation = forwardRef(({
  isLoading,
  paymentInfo,
  handleChange,
  ccConsent,
  handleConsentCheckboxChange,
  paymentInfoErrors,
  displayCardChargeMessage,
  splitCardContainer,
  fullWidthInputFields,
  updateElementRefs,
  hideNameFields, // #genai Add-Card page renders First/Last name in Billing Information instead
  semiBoldLabels, // #genai Phase 2 Add-Card: Card type / Card number / Expiration date labels are SemiBold per Figma 269:21301
}, ref) => {
  const classes = PaymentInformationStyles();
  const { t } = useTranslation();
  const [microform, setMicroform] = useState(null);
  const [isContainersReady, setIsContainersReady] = useState(false);
  const [isEditingCard, setIsEditingCard] = useState(false);
  const [captureContext, setCaptureContext] = useState(null);
  const currentCardTypeRef = useRef(null);
  const cardTypeUpdateTimeoutRef = useRef(null);
  const isUpdatingCardType = useRef(false);
  const isEditingCardRef = useRef(false);

  const currencyTypeId = useSelector(({ userData }) => userData.initParameters?.currencyTypeId);

  const elementRefInfo = {
    firstName: { id: 'firstName', elementRef: useRef(null), message: '' },
    lastName: { id: 'lastName', elementRef: useRef(null), message: '' },
    cardType: { id: 'cardType', elementRef: useRef(null), message: '' },
    cardNumber: { id: 'cardNumber', elementRef: useRef(null), message: '' },
    expirationDateMonth: { id: 'expirationDateMonth', elementRef: useRef(null), message: '' },
    expirationDateYear: { id: 'expirationDateYear', elementRef: useRef(null), message: '' },
    ccConsent: { id: 'ccConsent', elementRef: useRef(null), message: '' },
  };

  const flexRef = useRef(null);
  const scriptRef = useRef(null);

  // #genai - Extracted to reduce nesting depth
  const mapCardBrandToType = (brand) => {
    const brandMap = {
      visa: PAYMENT_CARD_TYPE_IDS.Visa,
      mastercard: PAYMENT_CARD_TYPE_IDS.MasterCard,
      amex: PAYMENT_CARD_TYPE_IDS.AmericanExpress,
      discover: PAYMENT_CARD_TYPE_IDS.Discover,
    };
    return brandMap[brand] || '';
  };

  const handleEdit = () => {
    if (captureContext) {
      setIsEditingCard(true);
    }
    if (!paymentInfo.isUpdated) {
      handleChange('isUpdated', { target: { value: true } });
    }
  };

  useEffect(() => {
    if (paymentInfo.isUpdated) {
      handleEdit();
    }
  }, [paymentInfo.isUpdated]);

  const generateAndStoreCaptureContext = async () => {
    const response = await generateCaptureContext(currencyTypeId);
    const { captureContext: context } = response;
    setCaptureContext(context);
  };

  // #genai - Extracted callback to reduce nesting depth
  const resetCardTypeUpdateFlag = () => {
    isUpdatingCardType.current = false;
  };

  // #genai - Extracted to reduce nesting depth
  const scheduleCardTypeUpdate = (detectedType) => {
    if (cardTypeUpdateTimeoutRef.current) {
      clearTimeout(cardTypeUpdateTimeoutRef.current);
    }

    cardTypeUpdateTimeoutRef.current = setTimeout(() => {
      isUpdatingCardType.current = true;
      currentCardTypeRef.current = detectedType;
      handleChange('cardType', { target: { value: detectedType } });
      setTimeout(resetCardTypeUpdateFlag, 100);
    }, 200);
  };

  // #genai - Extracted card number change handler to reduce nesting depth
  const handleCardNumberChange = (event) => {
    if (!event.card) return;

    const detectedType = mapCardBrandToType(event.card?.[0]?.name);
    const shouldUpdateCardType = detectedType
      && detectedType !== currentCardTypeRef.current
      && !isUpdatingCardType.current;

    if (shouldUpdateCardType) {
      scheduleCardTypeUpdate(detectedType);
    }
  };

  // #genai - Extracted microform field setup to reduce nesting depth
  const setupCardNumberField = (microformInstance) => {
    const cardNumber = microformInstance.createField('number', {
      style: {
        base: {
          fontSize: '18px',
          color: '#1B202C',
        },
      },
    });

    elementRefInfo.cardNumber.elementRef.current = cardNumber;
    cardNumber.on('change', handleCardNumberChange);
    cardNumber.innerHTML = '';
    cardNumber.load('#cardNumber');

    return microformInstance;
  };

  // #genai - Extracted script onload handler to reduce nesting depth
  const handleFlexScriptLoad = () => {
    if (!globalThis.Flex) {
      throw new Error('Flex library not available');
    }

    const flex = new globalThis.Flex(captureContext);
    flexRef.current = flex;

    const microformInstance = flex.microform('card');
    setupCardNumberField(microformInstance);
    setMicroform(microformInstance);
  };

  const initializeMicroform = async () => {
    if (!captureContext || !isEditingCard) {
      return;
    }

    const decodedContext = decodeCaptureContext(captureContext);
    const { clientLibrary, clientLibraryIntegrity } = decodedContext.ctx[0].data;

    const script = document.createElement('script');
    script.src = clientLibrary;
    script.type = 'text/javascript';
    script.async = true;
    script.integrity = clientLibraryIntegrity;
    script.crossOrigin = 'anonymous';
    script.onload = handleFlexScriptLoad;

    document.head.appendChild(script);
    scriptRef.current = script;
  };

  const checkContainers = () => {
    const cardNumberContainer = document.getElementById('cardNumber');
    if (cardNumberContainer) {
      setIsContainersReady(true);
    } else {
      setTimeout(checkContainers, 100);
    }
  };

  // #genai - Extracted useEffect cleanup to reduce the outer component's cognitive complexity
  // (SonarQube S3776). Behaviour identical to the inline cleanup it replaces.
  const cleanupMicroformScript = () => {
    if (scriptRef.current) {
      scriptRef.current.remove();
    }
    if (cardTypeUpdateTimeoutRef.current) {
      clearTimeout(cardTypeUpdateTimeoutRef.current);
    }
  };

  useEffect(() => {
    if (updateElementRefs == null) return;
    updateElementRefs(elementRefInfo);
  }, []);

  useEffect(() => {
    generateAndStoreCaptureContext();
  }, []);

  useEffect(() => {
    checkContainers();
  }, [isEditingCard]);

  useEffect(() => {
    if (isContainersReady && captureContext) {
      initializeMicroform();
      return cleanupMicroformScript;
    }
    return undefined;
  }, [isContainersReady, captureContext, isEditingCard]);

  useEffect(() => {
    currentCardTypeRef.current = paymentInfo.cardType;
  }, [paymentInfo.cardType]);

  useEffect(() => {
    isEditingCardRef.current = isEditingCard;
  }, [isEditingCard]);

  useEffect(() => {
    if (isLoading) {
      setIsEditingCard(false);
    }
  }, [isLoading]);

  const handleTokenisation = async () => {
    if (!isEditingCardRef.current) {
      return { transientToken: null, isCardNumberUpdated: false };
    }
    const options = getTokenOptions(paymentInfo);
    const tokenData = await new Promise((resolve) => {
      microform.createToken(options, (err, token) => {
        if (err) {
          resolve(null);
          return;
        }

        resolve(token);
      });
    });

    return { transientToken: tokenData, isCardNumberUpdated: isEditingCardRef.current };
  };

  // Expose the tokenization function through ref
  useImperativeHandle(ref, () => ({
    tokenize: handleTokenisation,
    setIsEditingCard,
  }));

  if (isLoading) {
    return (
      <PaymentInformationLoader splitCardContainer={splitCardContainer} fullWidthInputFields={fullWidthInputFields} />
    );
  }
  return (
    <EVGrid container item direction="column" className={classes.paymentInfoContainer}>
      {/* #genai First/Last name hidden when the consumer (Phase 2 Add-Card page) renders them in
          the Billing Information section instead. Consolidated into a single `!hideNameFields`
          fragment (previously two adjacent guards) to reduce the outer render's cognitive
          complexity (SonarQube S3776). Rendered output is byte-identical. */}
      {!hideNameFields && (
        <>
          <EVGrid className={classes.fieldContainer} item>
            <TextInputField
              inputId="firstName"
              inputValue={paymentInfo.firstName}
              size="small"
              handleChange={(event) => handleChange('firstName', event)}
              inputLabelProps={{
                className: classes.label,
                classes: { asterisk: classes.asterisk },
                required: true,
                error: paymentInfoErrors.firstName.length > 0,
              }}
              inputFieldProps={{
                fullWidth: true,
                inputProps: { maxLength: 64 },
                classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
                FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
                inputRef: elementRefInfo.firstName.elementRef,
                autoComplete: 'off',
              }}
              labelText={t('payment_information__first_name')}
              helperText={paymentInfoErrors.firstName}
            />
          </EVGrid>
          <EVGrid className={classes.fieldContainer} item>
            <TextInputField
              inputId="lastName"
              inputValue={paymentInfo.lastName}
              size="small"
              handleChange={(event) => handleChange('lastName', event)}
              inputLabelProps={{
                className: classes.label,
                classes: { asterisk: classes.asterisk },
                required: true,
                error: paymentInfoErrors.lastName.length > 0,
              }}
              inputFieldProps={{
                fullWidth: true,
                inputProps: { maxLength: 64 },
                classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
                FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
                inputRef: elementRefInfo.lastName.elementRef,
                autoComplete: 'off',
              }}
              labelText={t('payment_information__last_name')}
              helperText={paymentInfoErrors.lastName}
            />
          </EVGrid>
        </>
      )}
      <EVGrid className={classes.fieldContainer} container item direction="column">
        <EVGrid className={splitCardContainer ? classes.cardContainerSplit : classes.cardContainer} container item>
          <EVGrid className={classes.cardTypeContainer} item>
            {/* #genai Phase 2 field-error UX (semiBoldLabels ⇒ Add-Card page, Figma 269:21301):
                labels stay charcoal on error — only the input box + helper text turn red. Phase 1
                (semiBoldLabels=false) continues to also color the label red. */}
            <EVInputLabel
              className={semiBoldLabels ? classes.phase2Label : classes.label}
              classes={{ asterisk: classes.asterisk }}
              required
              error={!semiBoldLabels && paymentInfoErrors.cardType.length > 0}
            >
              {t('payment_information__card_type')}
            </EVInputLabel>
            <SelectField
              selectId="cardType"
              variant="outlined"
              fullWidth
              selectedValue={paymentInfo.cardType}
              handleChange={(event) => handleChange('cardType', event)}
              className={classes.cardTypeOptions}
              selectOptions={PAYMENT_CARD_TYPE_OPTION}
              error={paymentInfoErrors.cardType.length > 0}
              errorMessage={paymentInfoErrors.cardType}
              innerRef={elementRefInfo.cardType.elementRef}
            />
          </EVGrid>
          <EVGrid
            id="cardNumberContainer"
            className={fullWidthInputFields ? classes.cardNumberContainerFullWidth : classes.cardNumberContainer}
            item
          >
            <EVInputLabel className={semiBoldLabels ? classes.phase2Label : classes.label} classes={{ asterisk: classes.asterisk }} required>
              {t('payment_information__card_number')}
            </EVInputLabel>
            {isEditingCard ? (
              <>
                <div
                  id="cardNumber"
                  className={semiBoldLabels && paymentInfoErrors.cardNumber.length > 0
                    ? `${classes.cardNumberInputText} ${classes.hostedFieldError}`
                    : classes.cardNumberInputText}
                />
                {paymentInfoErrors.cardNumber && (
                  <EVFormHelperText error className={classes.cardInputHelperText}>
                    {paymentInfoErrors.cardNumber}
                  </EVFormHelperText>
                )}
              </>
            ) : (
              <TextInputField
                inputId="cardNumber"
                inputValue={paymentInfo.cardNumber}
                size="small"
                inputLabelProps={{
                  className: classes.label,
                  style: {
                    display: 'none',
                  },
                  classes: { asterisk: classes.asterisk },
                  required: true,
                  error: paymentInfoErrors.cardNumber.length > 0,
                }}
                inputFieldProps={{
                  fullWidth: true,
                  inputProps: { maxLength: 25 },
                  classes: { root: fullWidthInputFields ? classes.cardInputTextFullWidth : classes.cardInputText },
                  FormHelperTextProps: { classes: { error: classes.cardInputHelperText } },
                  inputRef: elementRefInfo.cardNumber.elementRef,
                  autoComplete: 'off',
                  onFocus: handleEdit,
                }}
                labelText={t('payment_information__card_number')}
                helperText={paymentInfoErrors.cardNumber}
              />
            )}
          </EVGrid>
        </EVGrid>
      </EVGrid>
      <CardExpirationSection
        paymentInfo={paymentInfo}
        paymentInfoErrors={paymentInfoErrors}
        handleChange={handleChange}
        semiBoldLabels={semiBoldLabels}
        elementRefInfo={elementRefInfo}
        classes={classes}
        t={t}
      />
      <EVGrid className={classes.ccConsentContainer} item>
        {displayCardChargeMessage ? (
          <EVFormControl error={paymentInfoErrors.ccConsent?.length > 0} component="fieldset">
            <LabelledCheckbox
              checkboxId="saveCardData"
              handleCheckboxSelectChange={handleConsentCheckboxChange}
              checked={ccConsent}
              checkboxValue={ccConsent}
              checkboxLabel={t('payment_information__consent')}
              className={classes.checkboxContainer}
              inputRef={elementRefInfo.ccConsent.elementRef}
            />
            <EVTypography className={classes.cardChargeMessage}>
              {t('payment_information__card_charge_message')}
            </EVTypography>
            <EVFormHelperText className={classes.ccErrorHelperText}>{paymentInfoErrors.ccConsent}</EVFormHelperText>
          </EVFormControl>
        ) : (
          <LabelledCheckbox
            checkboxId="saveCardData"
            handleCheckboxSelectChange={handleConsentCheckboxChange}
            checked={ccConsent}
            checkboxValue={ccConsent}
            checkboxLabel={t('payment_information__consent')}
            className={classes.checkboxContainer}
            inputRef={elementRefInfo.ccConsent.elementRef}
          />
        )}
      </EVGrid>
    </EVGrid>
  );
});

PaymentInformation.defaultProps = {
  displayCardChargeMessage: false,
  splitCardContainer: false,
  fullWidthInputFields: false,
  updateElementRefs: null,
  hideNameFields: false, // #genai
  semiBoldLabels: false, // #genai
};

PaymentInformation.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  paymentInfo: PropTypes.shape().isRequired,
  handleChange: PropTypes.func.isRequired,
  ccConsent: PropTypes.bool.isRequired,
  handleConsentCheckboxChange: PropTypes.func.isRequired,
  paymentInfoErrors: PropTypes.shape().isRequired,
  displayCardChargeMessage: PropTypes.bool,
  splitCardContainer: PropTypes.bool,
  fullWidthInputFields: PropTypes.bool,
  updateElementRefs: PropTypes.func,
  hideNameFields: PropTypes.bool, // #genai
  semiBoldLabels: PropTypes.bool, // #genai
};

export default PaymentInformation;
